import * as THREE from 'three';
import {
    ACESFilmicToneMapping,
    BackSide,
    CineonToneMapping,
    Color, CustomToneMapping,
    DepthTexture,
    DirectionalLight,
    DoubleSide, FloatType,
    Fog,
    FrontSide,
    GLSL3, GreaterDepth,
    HalfFloatType, LessEqualDepth,
    LinearEncoding, LinearFilter, LinearMipmapLinearFilter,
    LinearSRGBColorSpace,
    LinearToneMapping,
    LoadingManager, LuminanceFormat,
    Mesh,
    MeshBasicMaterial,
    MeshPhysicalMaterial, MeshStandardMaterial, NoColorSpace,
    NoToneMapping,
    PMREMGenerator, RawShaderMaterial,
    Raycaster,
    ReinhardToneMapping,
    RGBAFormat,
    ShaderMaterial,
    SRGBColorSpace,
    TextureLoader,
    Vector2,
    Vector3,
    WebGLRenderTarget
} from 'three';
import {resizeRendererToDisplaySize} from '../libs/three-utils';
import {GLTFLoader} from 'three/addons/loaders/GLTFLoader.js';
import {OrbitControls} from '../libs/OrbitControls.js';
import { HDRCubeTextureLoader } from 'three/addons/loaders/HDRCubeTextureLoader.js';
import {RGBELoader} from 'three/addons/loaders/RGBELoader.js';
import CustomShaderMaterial from 'three-custom-shader-material/vanilla';
import quadVert from './shader/quad.vert.glsl';
import copyFrag from './shader/copy.frag.glsl';
import liquidVert from './shader/liquid.vert.glsl';
import liquidFrag from './shader/liquid.frag.glsl';
import flaconVert from './shader/flacon.vert.glsl';
import flaconFrag from './shader/flacon.frag.glsl';
import blurFrag from './shader/blur.frag.glsl';
import compositeFrag from './shader/composite.frag.glsl'
import {label} from 'three/nodes';

// the target duration of one frame in milliseconds
const TARGET_FRAME_DURATION_MS = 16;

// total time
let time = 0;

// duration between the previous and the current animation frame
let deltaTimeMS = 0;

// total framecount according to the target frame duration
let frames = 0;

// relative frames according to the target frame duration (1 = 60 fps)
// gets smaller with higher framerates --> use to adapt animation timing
let deltaFrames = 0;

const settings = {
}

// module variables
let _isDev,
    _pane,
    _isInitialized = false,
    camera,
    scene,
    renderer,
    controls,
    raycaster,
    glbScene,
    flaconMaterial,
    liquidMaterial,
    hdrEquiMap,
    hdrEquiMapRT,
    backdropTexture,
    glassOuterThicknessTexture,
    glassOuterNormalTexture,
    viewportSize;

let flaconMesh, liquidMesh, labelMesh, capMesh, hexLatticeMesh;

let rt1, rt2, rt3, rt4, quadMesh, copyIntermediateMaterial, blurMaterial, compositeMaterial, copyFinalMaterial;

const blurTargetSizeFactor = 0.25;

const cutoutMaterial = new MeshBasicMaterial( {
    colorWrite: false,
    side: DoubleSide
})

function init(canvas, onInit = null, isDev = false, pane = null) {
    _isDev = isDev;
    _pane = pane;

    if (pane) {
    }

    const manager = new LoadingManager();

    const objLoader = new GLTFLoader(manager);
    objLoader.load((new URL('../assets/scene.glb', import.meta.url)).toString(), (gltf) => {
        glbScene = (gltf.scene)
    }, null, console.log);

    hdrEquiMap = new RGBELoader(manager)
        .load((new URL('../assets/env03.hdr', import.meta.url)).toString())

    backdropTexture = new TextureLoader(manager)
        .load((new URL('../assets/backdrop0.jpg', import.meta.url)).toString())

    glassOuterThicknessTexture = new TextureLoader(manager)
        .load((new URL('../assets/glass-outer-thickness.png', import.meta.url)).toString())

    glassOuterNormalTexture = new TextureLoader(manager)
        .load((new URL('../assets/glass-outer-normal.png', import.meta.url)).toString())

    manager.onLoad = () => {
        setupScene(canvas);

        if (onInit) onInit(this);

        renderer.setAnimationLoop((t) => run(t));

        resize();
    }
}

function setupScene(canvas) {
    camera = new THREE.PerspectiveCamera( 30, window.innerWidth / window.innerHeight, .2, .4);
    camera.position.set(0, 0.02, 0.31);
    //camera.position.set(-0.31, 0.1, 0);
    camera.lookAt(new Vector3());

    scene = new THREE.Scene();
    scene.add(glbScene);
    glbScene.position.y = - 0.065;
    glbScene.rotation.y = Math.PI / 2;
    glbScene.scale.multiplyScalar(0.0012);
   // glbScene.rotation.y = -Math.PI / 2;
    const light = new DirectionalLight();
    light.position.set(-10, 10, 0);
    //scene.add(light);
    //scene.fog = new Fog(0xff0000, 0.3, 0.31);
    //scene.overrideMaterial = new MeshBasicMaterial();

    // Set CustomToneMapping to Uncharted2
    // source: http://filmicworlds.com/blog/filmic-tonemapping-operators/

    THREE.ShaderChunk.tonemapping_pars_fragment = THREE.ShaderChunk.tonemapping_pars_fragment.replace(
        'vec3 CustomToneMapping( vec3 color ) { return color; }',
        `#define Uncharted2Helper( x ) max( ( ( x * ( 0.15 * x + 0.10 * 0.50 ) + 0.20 * 0.02 ) / ( x * ( 0.15 * x + 0.50 ) + 0.20 * 0.30 ) ) - 0.02 / 0.30, vec3( 0.0 ) )
					float toneMappingWhitePoint = 1.;
					vec3 CustomToneMapping( vec3 color ) {
						color *= toneMappingExposure;
						return saturate( Uncharted2Helper( color ) / Uncharted2Helper( vec3( toneMappingWhitePoint ) ) );
					}`
    );

    THREE.ShaderLib.physical.fragmentShader = THREE.ShaderLib.physical.fragmentShader.replace(
        '#include <clipping_planes_pars_fragment>',
        `
        #include <clipping_planes_pars_fragment>
        #include <tonemapping_pars_fragment>
        `
    );
    THREE.ShaderLib.physical.fragmentShader = THREE.ShaderLib.physical.fragmentShader.replace(
        'vec3 totalSpecular = reflectedLight.directSpecular + reflectedLight.indirectSpecular;',
        `
        vec3 totalSpecular = reflectedLight.directSpecular + reflectedLight.indirectSpecular;
        #ifdef IS_FLACON
        //totalSpecular = totalSpecular + (totalSpecular - .5 ) * 1.5 + .5;
        #endif
        #ifdef CUSTOM_TONE_MAPPING
            totalSpecular = ACESFilmicToneMapping(totalSpecular);
        #endif
        `
    );
    THREE.ShaderChunk.transmission_pars_fragment = THREE.ShaderChunk.transmission_pars_fragment.replace(
        'vec4 transmittedLight = getTransmissionSample( refractionCoords, roughness, ior );',
        `
        vec4 transmittedLight = getTransmissionSample( refractionCoords, roughness, ior );
        #ifdef IS_FLACON
        transmittedLight.r = getTransmissionSample( refractionCoords + vec2(0.0005, 0.), roughness, ior ).r;
        transmittedLight.b = getTransmissionSample( refractionCoords - vec2(0.001, 0.), roughness, ior ).b;
        #endif
        `
    );

    renderer = new THREE.WebGLRenderer( { canvas, antialias: true } );
    renderer.sortObjects = true;
    renderer.useLegacyLights = false;
    renderer.toneMapping = NoToneMapping;
    renderer.toneMappingExposure = 1;
    THREE.ColorManagement.enabled = true;
    document.body.appendChild( renderer.domElement );
    viewportSize = new Vector2(renderer.domElement.clientWidth, renderer.domElement.clientHeight);

    const pmremGenerator = new PMREMGenerator( renderer );
    pmremGenerator.compileEquirectangularShader();

    hdrEquiMap.colorSpace = LinearSRGBColorSpace;
    hdrEquiMap.mapping = THREE.EquirectangularReflectionMapping;
    hdrEquiMapRT = pmremGenerator.fromEquirectangular(hdrEquiMap);
    hdrEquiMapRT.colorSpace = LinearSRGBColorSpace;
    backdropTexture.colorSpace = SRGBColorSpace;

    controls = new OrbitControls(camera, renderer.domElement);
    controls.enableZoom = controls.enablePan = false;
    controls.enableDamping = true;
    /*controls.minAzimuthAngle = -Math.PI / 3;
    controls.maxAzimuthAngle = Math.PI / 3;*/
    controls.minPolarAngle = Math.PI / 8;
    controls.maxPolarAngle = (7 * Math.PI) / 8;
    controls.update();

    raycaster = new Raycaster();

    scene.environment = hdrEquiMapRT.texture;

    flaconMesh = glbScene.getObjectByName('glass-outer');
    liquidMesh = glbScene.getObjectByName('glass-inner');
    labelMesh = glbScene.getObjectByName('label');
    capMesh = glbScene.getObjectByName('cap');
    hexLatticeMesh = glbScene.getObjectByName('hex-lattice');

    glassOuterThicknessTexture.flipY = false;
    flaconMaterial = new CustomShaderMaterial({
        baseMaterial: MeshPhysicalMaterial,
        vertexShader: flaconVert,
        fragmentShader: flaconFrag,
        silent: true, // Disables the default warning if true
        uniforms: {
            uFakeShadowStrength: { value: 0 },
            uFresnelStrength: { value: 0 }
        },
        defines: {
            'PHYSICAL': '',
            'IS_FLACON': ''
        },
        envMap: hdrEquiMapRT.texture,
        envMapIntensity: 1,
        color: 0xffffff,
        reflectivity: 1.,
        roughness: 0.0,
        transmission: 1,
        thickness: 20,
        thicknessMap: glassOuterThicknessTexture,
        normalMap: glassOuterNormalTexture,
        normalScale: new Vector2(.15, .15),
        ior: 1.5,
        opacity: 1,
        transparent: true,
        fog: false,
        specularIntensity: .5,
    });
    flaconMesh.material = flaconMaterial;

    THREE.ShaderChunk.transmission_fragment = THREE.ShaderChunk.transmission_fragment.replace(
        'material.transmission = transmission;',
        `
        material.transmission = transmission;
        #ifdef IS_LIQUID
            material.transmission = customTransmission;
        #endif
        `
    );

    liquidMaterial = new CustomShaderMaterial({
        baseMaterial: MeshPhysicalMaterial,
        vertexShader: liquidVert,
        fragmentShader: liquidFrag,
        silent: true, // Disables the default warning if true
        uniforms: {
            uDepthTexture: { value: null }
        },
        defines: {
            'PHYSICAL': '',
            'IS_LIQUID': ''
        },
        envMap: hdrEquiMapRT.texture,
        envMapIntensity: 1,
        color: 0xf4f0d3,
        reflectivity: .3,
        roughness: 0.0,
        transmission: 1,
        thickness: 30,
        ior: 1.3,
        opacity: 1,
        transparent: true,
        specularIntensity: 1
    })
    liquidMesh.material = liquidMaterial;

    copyIntermediateMaterial = new ShaderMaterial({
        uniforms: {
            uTexture: {value: null},
            uResolution: {value: viewportSize}
        },
        vertexShader: quadVert,
        fragmentShader: copyFrag,
        depthWrite: false,
        depthTest: false,
        stencilWrite: false,
        glslVersion: GLSL3,
        toneMapped: false
    });
    copyFinalMaterial = new ShaderMaterial({
        uniforms: {
            uTexture: {value: null},
            uResolution: {value: viewportSize}
        },
        defines: {
            FINAL: ''
        },
        vertexShader: quadVert,
        fragmentShader: copyFrag,
        depthWrite: false,
        depthTest: false,
        stencilWrite: false,
        glslVersion: GLSL3,
        toneMapped: false
    });
    blurMaterial = new ShaderMaterial({
        uniforms: {
            uColor: {value: null},
            uDirection: {value: new Vector2()}
        },
        vertexShader: quadVert,
        fragmentShader: blurFrag,
        depthWrite: false,
        depthTest: false,
        stencilWrite: false,
        glslVersion: GLSL3,
        toneMapped: false
    });
    compositeMaterial = new ShaderMaterial({
        uniforms: {
            uColor: {value: null},
            uBloom: {value: null}
        },
        vertexShader: quadVert,
        fragmentShader: compositeFrag,
        depthWrite: false,
        depthTest: false,
        stencilWrite: false,
        glslVersion: GLSL3,
        toneMapped: false
    });

    const quadGeo = new THREE.BufferGeometry();
    quadGeo.setAttribute( 'position', new THREE.Float32BufferAttribute( [ - 1, 3, 0, - 1, - 1, 0, 3, - 1, 0 ], 3 ) );
    quadGeo.setAttribute( 'uv', new THREE.Float32BufferAttribute( [ 0, 2, 0, 0, 2, 0 ], 2 ) );
    quadMesh = new Mesh(quadGeo, copyIntermediateMaterial);
    quadMesh.visible = false;
    quadMesh.name = 'quad';
    scene.add(quadMesh);

    quadMesh.renderOrder = 0;
    liquidMesh.renderOrder = flaconMesh.renderOrder = 2;
    liquidMesh.groupOrder = flaconMesh.groupOrder = 2;
    capMesh.renderOrder = hexLatticeMesh.renderOrder = labelMesh.renderOrder = 3;
    capMesh.groupOrder = hexLatticeMesh.groupOrder = labelMesh.groupOrder = 3;
    capMesh.name = 'cap';
    hexLatticeMesh.name = 'hex-lattice';
    labelMesh.name = 'label';

    capMesh.material.envMapIntensity = 1;

    rt1 = new WebGLRenderTarget(viewportSize.x, viewportSize.y, { depthBuffer: true, depthTexture: new DepthTexture(), samples: 2});
    rt2 = new WebGLRenderTarget(viewportSize.x, viewportSize.y, { depthBuffer: true, depthTexture: new DepthTexture(), samples: 2});
    rt1.texture.colorSpace = LinearSRGBColorSpace;
    rt2.texture.colorSpace = LinearSRGBColorSpace;

    rt3 = new WebGLRenderTarget(viewportSize.x * blurTargetSizeFactor, viewportSize.y * blurTargetSizeFactor, { depthBuffer: true});
    rt3.texture.type = HalfFloatType;
    rt3.texture.internalFormat = 'RGBA16F';
    rt3.texture.colorSpace = LinearSRGBColorSpace;

    rt4 = new WebGLRenderTarget(viewportSize.x * blurTargetSizeFactor, viewportSize.y * blurTargetSizeFactor, { depthBuffer: true});
    rt4.texture.type = HalfFloatType;
    rt4.texture.internalFormat = 'RGBA16F';
    rt4.texture.colorSpace = LinearSRGBColorSpace;

    _isInitialized = true;
}

function run(t = 0) {
    deltaTimeMS = Math.min(TARGET_FRAME_DURATION_MS, t - time);
    time = t;
    deltaFrames = deltaTimeMS / TARGET_FRAME_DURATION_MS;
    frames += deltaFrames;

    animate();
    render();
}

function resize() {
    if (!_isInitialized) return;

    if (resizeRendererToDisplaySize(renderer)) {
        renderer.getSize(viewportSize);
        camera.aspect = viewportSize.x / viewportSize.y;
        camera.updateProjectionMatrix();

        rt1.setSize(viewportSize.x, viewportSize.y);
        rt2.setSize(viewportSize.x, viewportSize.y);
        rt3.setSize(viewportSize.x * blurTargetSizeFactor, viewportSize.y * blurTargetSizeFactor);
        rt4.setSize(viewportSize.x * blurTargetSizeFactor, viewportSize.y * blurTargetSizeFactor);

        copyIntermediateMaterial.uniforms.uResolution.value = viewportSize;
        copyFinalMaterial.uniforms.uResolution.value = viewportSize;
    }
}

function animate() {
    if (controls) controls.update();
}

function render() {

    renderer.setRenderTarget(rt2);
    flaconMesh.material.defines.CUSTOM_TONE_MAPPING = '';
    liquidMesh.material.defines.CUSTOM_TONE_MAPPING = '';
    labelMesh.material.defines = capMesh.material.defines = hexLatticeMesh.material.defines = { CUSTOM_TONE_MAPPING: ''};
    labelMesh.visible = capMesh.visible = hexLatticeMesh.visible = false;
    flaconMesh.visible = false;
    quadMesh.visible = true;
    quadMesh.material = copyIntermediateMaterial;
    quadMesh.material.toneMapped = false;
    quadMesh.material.uniforms.uTexture.value = backdropTexture;
    renderer.autoClear = false;
    renderer.render( scene, camera );
    quadMesh.visible = false;
    flaconMesh.visible = true;
    flaconMesh.material = cutoutMaterial;
    renderer.render( scene, camera );
    flaconMesh.material = flaconMaterial;
    flaconMesh.visible = false;

    renderer.sortObjects = true;
    labelMesh.visible = capMesh.visible = hexLatticeMesh.visible = true;
    labelMesh.material.depthFunc = capMesh.material.depthFunc = hexLatticeMesh.material.depthFunc = GreaterDepth;
    liquidMesh.visible = false;
    renderer.render( scene, camera );
    labelMesh.material.depthFunc = capMesh.material.depthFunc = hexLatticeMesh.material.depthFunc = LessEqualDepth;
    renderer.sortObjects = false;
    renderer.autoClear = true;

    renderer.setRenderTarget(rt1);
    labelMesh.visible = capMesh.visible = hexLatticeMesh.visible = false;
    flaconMesh.visible = true;
    flaconMesh.material.color = new Color(0xffffff);
    flaconMesh.material.side = BackSide;
    flaconMesh.material.uniforms.uFakeShadowStrength.value = .2;
    quadMesh.visible = true;
    quadMesh.material.uniforms.uTexture.value = rt2.texture;
    renderer.render( scene, camera );

    renderer.setRenderTarget(rt2);
    quadMesh.visible = true;
    quadMesh.material.uniforms.uTexture.value = rt1.texture;
    flaconMesh.visible = false;
    liquidMesh.visible = true;
    liquidMesh.material.uniforms.uDepthTexture.value = rt1.depthTexture;
    renderer.render( scene, camera );

    renderer.setRenderTarget(rt1);
    renderer.autoClear = false;
    quadMesh.visible = true;
    quadMesh.material = copyIntermediateMaterial;
    flaconMesh.visible = true;
    flaconMesh.material.side = FrontSide;
    flaconMesh.material.uniforms.uFakeShadowStrength.value = 0.;
    flaconMesh.material.uniforms.uFresnelStrength.value = 4.;
    flaconMesh.material.needsUpdate = true;
    liquidMesh.visible = false;
    quadMesh.material.uniforms.uTexture.value = rt2.texture;
    renderer.render( scene, camera );

    flaconMesh.visible = false;
    quadMesh.visible = false;
    liquidMesh.visible = true;
    liquidMesh.material = cutoutMaterial;
    labelMesh.visible = capMesh.visible = hexLatticeMesh.visible = true;
    renderer.render( scene, camera );
    renderer.autoClear = true;
    liquidMesh.material = liquidMaterial;

    renderer.setRenderTarget(rt3);
    delete flaconMesh.material.defines.CUSTOM_TONE_MAPPING;
    delete liquidMesh.material.defines.CUSTOM_TONE_MAPPING;
    labelMesh.material.defines = capMesh.material.defines = hexLatticeMesh.material.defines = { };
    capMesh.material.needsUpdate = labelMesh.material.needsUpdate = hexLatticeMesh.material.needsUpdate = flaconMesh.material.needsUpdate = true;
    quadMesh.visible = false;
    labelMesh.visible = false;
    liquidMesh.visible = false;
    flaconMesh.visible = false;
    flaconMesh.material.uniforms.uFresnelStrength.value = 0.;
    flaconMesh.material.color = new Color(0);
    flaconMesh.material.transparent = false;
    flaconMesh.material.transmission = 0;
    flaconMesh.visible = capMesh.visible = hexLatticeMesh.visible = labelMesh.visible = true;
    renderer.render( scene, camera );

    renderer.setRenderTarget(rt4);
    quadMesh.visible = true;
    liquidMesh.visible = flaconMesh.visible = labelMesh.visible = capMesh.visible = hexLatticeMesh.visible = false;
    quadMesh.material = blurMaterial;
    blurMaterial.uniforms.uColor.value = rt3.texture;
    blurMaterial.uniforms.uDirection.value.set(1., 0)
    renderer.render( scene, camera );

    renderer.setRenderTarget(rt3);
    quadMesh.visible = true;
    liquidMesh.visible = flaconMesh.visible = labelMesh.visible = capMesh.visible = hexLatticeMesh.visible = false;
    quadMesh.material = blurMaterial;
    blurMaterial.uniforms.uColor.value = rt4.texture;
    blurMaterial.uniforms.uDirection.value.set(0, 1.)
    renderer.render( scene, camera );

    renderer.setRenderTarget(null);
    quadMesh.visible = true;
    flaconMesh.material.transparent = false;
    flaconMesh.material.transmission = 1;
    liquidMesh.visible = flaconMesh.visible = labelMesh.visible = capMesh.visible = hexLatticeMesh.visible = false;
    quadMesh.material = compositeMaterial;
    compositeMaterial.uniforms.uColor.value = rt1.texture;
    compositeMaterial.uniforms.uBloom.value = rt3.texture;
    renderer.render( scene, camera );
}

export default {
    init,
    run,
    resize
}
